import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    this.applyPosition(this.getBoundingClientRect());
  };

  reposition() {
    this.#updatePosition();
  }

  /**
   * @param {DOMRect} rect - This panel's rect, measured before any writes.
   */
  applyPosition(rect) {
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  }

  #mutationObserver = new MutationObserver(this.#updatePosition);

  startObserving() {
    this.#mutationObserver.observe(this, { attributes: true });
  }

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    queueInitialPosition(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

/**
 * Initial positioning for every panel on the page in one idle pass: all rects
 * are read before any panel is moved, so the page lays out once rather than
 * once per panel (a collection page has ~18 of them).
 * @type {Set<FloatingPanelComponent>}
 */
const initialQueue = new Set();

/** @param {FloatingPanelComponent} panel */
function queueInitialPosition(panel) {
  if (initialQueue.size === 0) requestIdleCallback(flushInitialPositions);
  initialQueue.add(panel);
}

async function flushInitialPositions() {
  if (viewTransition.current) await viewTransition.current;

  const panels = [...initialQueue].filter((panel) => panel.isConnected);
  initialQueue.clear();

  // Reading a rect forces a full-page layout, and a panel that lives in a <details> (every filter and sort
  // dropdown) is either closed, display:none on this viewport, or inside a closed dialog: nothing to measure
  // until the shopper opens it. So set the static offset now and measure on the details' first toggle.
  const isHidden = (panel) => {
    if (panel.closest('details')) return true;
    const dialog = panel.closest('dialog');
    return dialog instanceof HTMLDialogElement && !dialog.open;
  };
  const hidden = panels.filter(isHidden);
  const visible = panels.filter((panel) => !isHidden(panel));

  hidden.forEach((panel) => {
    panel.style.top = OFFSET + 'px';
    panel.startObserving();
    const details = panel.closest('details');
    details?.addEventListener('toggle', () => {
      if (details.open) panel.reposition();
    });
    panel.closest('dialog')?.addEventListener('toggle', () => panel.reposition(), { once: true });
  });

  const rects = visible.map((panel) => panel.getBoundingClientRect());
  visible.forEach((panel, index) => {
    panel.applyPosition(rects[index]);
    panel.startObserving();
  });
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
